import { compact } from 'lodash';

import { Document, getDocumentBlob } from './getDocumentAsBlob';

type PrintFrame = {
   printFrame: VoidFunction;
   removeFrame: VoidFunction;
};

type PrintFrameOptions = {
   source: string;
   onload: (printFrame: PrintFrame) => void;
   onerror: VoidFunction;
};

const createPrintFrame = (options: PrintFrameOptions): void => {
   const printFrame = document.createElement('iframe');
   printFrame.name = 'print';
   printFrame.onload = () => {
      options.onload({
         printFrame: () => {
            printFrame.contentWindow!.print();
         },
         removeFrame: () => {
            printFrame.remove();
         },
      });
   };
   printFrame.tabIndex = 0;
   printFrame.style.display = 'contents';
   printFrame.onerror = options.onerror;
   printFrame.setAttribute('src', options.source);
   window.document.body.appendChild(printFrame);
};

type PrintableDocument = {
   print: (callback: VoidFunction) => void;
};

const preparePrintableDocument = async (document: Document): Promise<PrintableDocument> => {
   const documentAsBlob = await getDocumentBlob(document);
   const blobURL = URL.createObjectURL(documentAsBlob);

   return new Promise((resolve, reject) => {
      createPrintFrame({
         source: blobURL,
         onload: ({ printFrame, removeFrame }) => {
            URL.revokeObjectURL(blobURL);

            resolve({
               print: (callback) => {
                  window.addEventListener('pointerdown', removeFrame, { once: true });
                  window.addEventListener('afterprint', callback, { once: true });
                  window.addEventListener('focus', callback, { once: true });

                  printFrame();

                  if ('htmlBase64' in document && 'chrome' in window) {
                     window.dispatchEvent(new FocusEvent('afterprint'));
                  }
               },
            });
         },
         onerror: () => {
            reject(new Error('Error during document printing.'));
         },
      });
   });
};

export const printDocumentAsPdf = async (...documents: Document[]): Promise<void> => {
   const promiseSettledResults = await Promise.allSettled(documents.map(preparePrintableDocument));
   const printableDocuments = compact(
      promiseSettledResults.map((result) => {
         if (result.status === 'rejected') {
            return null;
         }

         return result.value;
      })
   );

   // eslint-disable-next-line no-unreachable-loop
   for await (const { print } of printableDocuments) {
      await new Promise<void>((resolve) => {
         print(resolve);
      });
   }
};
